import { useState } from "react";
import { X, ArrowRight, ArrowLeft, LayoutGrid, Map, ClipboardCheck, ShieldAlert, ShieldCheck, WifiOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { hasRole } from "../lib/roles";
import { Button } from "./ui/button";

const BASE_STEPS = [
  { icon: LayoutGrid, title: "Bienvenue", text: "Cette application pilote en temps réel l'état de sécurité des fronts de carrière. Découvrons ensemble les bases en quelques étapes." },
  { icon: LayoutGrid, title: "Accueil", text: "La page d'accueil affiche les KPI de synthèse et les alertes prioritaires : fronts suspendus, anomalies critiques, actions en retard." },
  { icon: Map, title: "Vue carrière", text: "Consultez la liste complète des fronts, triée par priorité ou par ordre géographique, avec leur statut actuel : Autorisée, Avec réserves ou Suspendue." },
  { icon: ShieldCheck, title: "Fiche d'un front", text: "Chaque front a une fiche détaillée avec 6 onglets : Aperçu, Inspections, Anomalies, Actions, Photos, Historique, et un graphique d'évolution sur 30 jours." },
  { icon: WifiOff, title: "Mode hors ligne", text: "Sans réseau, l'app continue de fonctionner avec les dernières données en cache. Les inspections réalisées hors ligne sont automatiquement synchronisées au retour du réseau." },
];

const INSPECTEUR_STEPS = [
  { icon: ClipboardCheck, title: "Réaliser une inspection", text: "Depuis la fiche d'un front, lancez une nouvelle inspection et répondez au questionnaire. Une anomalie critique suspend automatiquement le front — cette suspension ne peut JAMAIS être levée automatiquement." },
];

const RESPONSABLE_STEPS = [
  { icon: ShieldAlert, title: "Remise en exploitation", text: "Un front suspendu ne peut être remis en service que par un Responsable ou un Administrateur, via un workflow de validation dédié." },
];

const ADMIN_STEPS = [
  { icon: ShieldCheck, title: "Administration", text: "Gérez les utilisateurs, les fronts, les questions du checklist, les QR codes et le journal d'audit. Le sélecteur de site en haut vous permet de filtrer par usine ou de tout voir." },
];

export const OnboardingTour = () => {
  const { tourOpen, closeTour, user } = useAuth();
  const [step, setStep] = useState(0);

  if (!tourOpen || !user) return null;

  let steps = [...BASE_STEPS];
  if (hasRole(user, "INSPECTEUR", "CONDUCTEUR_ENGINS", "CHEF_EQUIPE", "CONTREMAITRE", "BOUTEFEU")) steps = [...steps, ...INSPECTEUR_STEPS];
  if (hasRole(user, "RESPONSABLE", "ADMINISTRATEUR")) steps = [...steps, ...RESPONSABLE_STEPS];
  if (hasRole(user, "ADMINISTRATEUR")) steps = [...steps, ...ADMIN_STEPS];

  const s = steps[Math.min(step, steps.length - 1)];
  const isLast = step === steps.length - 1;

  return (
    <div data-testid="onboarding-tour" className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-[#121820] border border-cyan-900/50 rounded-2xl p-6 space-y-5 relative">
        <button data-testid="onboarding-tour-close" onClick={closeTour} className="absolute top-4 right-4 text-slate-500 hover:text-slate-200">
          <X className="w-5 h-5" />
        </button>
        <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/40 flex items-center justify-center">
          <s.icon className="w-6 h-6 text-cyan-400" />
        </div>
        <div>
          <h3 className="text-lg font-black uppercase text-slate-100">{s.title}</h3>
          <p className="text-sm text-slate-400 mt-2 leading-relaxed">{s.text}</p>
        </div>
        <div className="flex items-center justify-between pt-2">
          <div className="flex gap-1.5" data-testid="onboarding-tour-dots">
            {steps.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === step ? "bg-cyan-400" : "bg-slate-700"}`} />
            ))}
          </div>
          <div className="flex gap-2">
            {step > 0 && (
              <Button data-testid="onboarding-tour-prev" variant="outline" size="sm" onClick={() => setStep(step - 1)} className="border-slate-700 text-xs">
                <ArrowLeft className="w-3.5 h-3.5" />
              </Button>
            )}
            {isLast ? (
              <Button data-testid="onboarding-tour-finish" size="sm" onClick={closeTour} className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase text-xs">
                Terminer
              </Button>
            ) : (
              <Button data-testid="onboarding-tour-next" size="sm" onClick={() => setStep(step + 1)} className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase text-xs">
                Suivant <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
